import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useDispatch,
  useSelector } from "react-redux";
import {
  setCurrentPage,
  setPageSize,
  } from "../../../../redux/pointMechanisms/pointMechanisms";
import {
  getListPointMechanisms,
  deletePointMechanisms,
  } from "../../../../api/pointMechanisms/pointMechanisms";
import {
  PlusCircleOutlined,
  InfoCircleOutlined,
  ShopOutlined,
  } from "@ant-design/icons";
import {
  Button,
  Empty,
  Table,
  message,
  Tooltip,
  TreeSelect,
  Modal,
  Space,
  Typography,
} from "antd";
import FormAddPointMechanisms from "./FormAddPointMechanisms";
import { transformData } from "../../../../utils/recursive";
import ButtonWithPermission from "../../../../components/ButtonWithPermission/ButtonWithPermission";
import useStoreData from "../../../../hooks/useStoreData";
import RowActions from "../../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../../components/cms/actions";
import { brand, neutral } from "../../../../theme/tokens";
import StatusTag from "../../../../components/cms/StatusTag";
import BrandedPageLayout from "../../../../components/Dashboard/BrandedPageLayout";
import ListToolbar from "../../../../components/cms/ListToolbar";
import CmsTableCard from "../../../../components/cms/CmsTableCard";

const { Text } = Typography;

const FILTER_DEFS = [{ key: "branch", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 } }];

const COLUMN_DEFS = [
  { key: "id", label: "ID" },
  { key: "name", label: "Tên cơ chế", fixed: true },
  { key: "service", label: "Tên dịch vụ" },
  { key: "branch", label: "Đơn vị" },
  { key: "reward_type", label: "Loại cơ chế" },
  { key: "zalo_event", label: "Sự kiện Zalo" },
  { key: "description", label: "Mô tả" },
  { key: "conversion_rate", label: "Tỷ lệ chuyển đổi" },
  { key: "value", label: "Giá trị tích lũy" },
  { key: "point_expiry_enabled", label: "Hạn dùng" },
  { key: "expires_in_days", label: "Số ngày hiệu lực" },
  { key: "operation", label: "Thao tác", fixed: true },
];
// const DEFAULT_VISIBLE_COLUMNS = COLUMN_DEFS.map((c) => c.key);
const DEFAULT_VISIBLE_COLUMNS = ["id", "name", "reward_type", "conversion_rate", "value", "operation"];

export default function PointMechanismsTable() {
  const {
    listPointMechanisms,
    isLoadPointMechanisms,
    currentPage,
    pageSize,
    totalRecords,
  } = useSelector((state) => state.persistedReducer.pointMechanismsSlice);

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [branchFilter, setBranchFilter] = useState(undefined);

  const openAdd = () => {
    setEditRecord(null);
    setIsModalOpen(true);
  };
  const openEdit = (record) => {
    setEditRecord(record);
    setIsModalOpen(true);
  };
  const handleOk = () => setIsModalOpen(false);
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditRecord(null);
  }, []);

  // Giữ bộ lọc đơn vị khi đổi trang / số dòng (trước đây đổi trang là mất lọc).
  useEffect(() => {
    dispatch(getListPointMechanisms({ currentPage, pageSize, branchId: branchFilter }));
  }, [dispatch, currentPage, pageSize, branchFilter]);

  const handlePageChange = (page) => dispatch(setCurrentPage(page));
  const handleSizeChange = useCallback(
    (current, size) => dispatch(setPageSize(size)),
    [dispatch]
  );

  const deleteRecord = async (id) => {
    try {
      await dispatch(deletePointMechanisms(id));
      dispatch(getListPointMechanisms({ currentPage, pageSize, branchId: branchFilter }));
    } catch (err) {
      message.error("Xóa cơ chế thất bại");
    }
  };

  const dataTree = transformData(allListStore?.data);
  const options = dataTree?.filter((item) => item.parent_id === 0);
  // Đổi bộ lọc -> về trang 1; useEffect ([currentPage, pageSize, branchFilter]) tự fetch.
  const handleSearch = (value) => {
    setBranchFilter(value);
    if (currentPage !== 1) dispatch(setCurrentPage(1));
  };

  const handleRefresh = () =>
    dispatch(getListPointMechanisms({ currentPage, pageSize, branchId: branchFilter }));

  const allColumns = useMemo(
    () => ({
      id: { title: "ID", dataIndex: "id", key: "id", width: 80 },
      name: {
        title: "Tên cơ chế",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        width: 240,
        render: (text, record) => (
          <a
            style={{ color: brand.primary, fontWeight: 600, textDecoration: "none" }}
            className="hover:underline"
            onClick={() => openEdit(record)}
          >
            {text}
          </a>
        ),
      },
      service: {
        title: "Tên dịch vụ",
        key: "service",
        align: "center",
        width: 180,
        render: (_, record) => record.service_connect?.name || "—",
      },
      branch: {
        title: "Đơn vị",
        key: "branch",
        width: 200,
        ellipsis: true,
        render: (_, record) => (
          <Space>
            <ShopOutlined style={{ color: neutral[400] }} />
            <Text>{record.m_branch?.name || "—"}</Text>
          </Space>
        ),
      },
      reward_type: {
        title: "Loại cơ chế",
        key: "reward_type",
        width: 200,
        render: (_, record) => record.reward_type?.name || "—",
      },
      zalo_event: {
        title: "Sự kiện Zalo",
        key: "zalo_event",
        width: 220,
        ellipsis: true,
        render: (_, record) => record.zalo_event?.name || "—",
      },
      description: {
        title: "Mô tả",
        dataIndex: "description",
        key: "description",
        width: 240,
        ellipsis: true,
        render: (text) => text || "—",
      },
      conversion_rate: {
        title: "Tỷ lệ chuyển đổi",
        dataIndex: "conversion_rate",
        key: "conversion_rate",
        align: "center",
        width: 150,
        render: (v) => <b>{Number(v).toLocaleString("vi-VN")}</b>,
      },
      value: {
        title: "Giá trị tích lũy",
        dataIndex: "value",
        key: "value",
        align: "center",
        width: 150,
        render: (v) => <b>{Number(v).toLocaleString("vi-VN")}</b>,
      },
      point_expiry_enabled: {
        title: "Hạn dùng",
        dataIndex: "point_expiry_enabled",
        key: "point_expiry_enabled",
        align: "center",
        width: 120,
        render: (val) =>
          val ? <StatusTag>Có hạn</StatusTag> : <StatusTag>Vĩnh viễn</StatusTag>,
      },
      expires_in_days: {
        title: "Số ngày hiệu lực",
        dataIndex: "expires_in_days",
        key: "expires_in_days",
        align: "center",
        width: 150,
        render: (val, record) => (record.point_expiry_enabled ? <Text>{val} ngày</Text> : <Text>—</Text>),
      },
      operation: {
        title: "Thao tác",
        key: "operation",
        align: "center",
        width: 100,
        fixed: "right",
        render: (_, record) => (
          <RowActions
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                onClick: () => openEdit(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa cơ chế",
                danger: true,
                divider: true,
                onClick: () =>
                  confirmDelete(record.name || `cơ chế #${record.id}`, () =>
                    deleteRecord(record.id)
                  ),
              },
            ]}
          />
        ),
      },
    }),
    // openEdit/deleteRecord ổn định trong render này
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, pageSize]
  );

  const displayColumns = useMemo(
    () =>
      COLUMN_DEFS.filter((def) => def.fixed || visibleColumns.includes(def.key)).map(
        (def) => allColumns[def.key]
      ),
    [visibleColumns, allColumns]
  );

  return (
    <BrandedPageLayout
      title="Quản lý cơ chế tích điểm"
      subtitle="Thiết lập và quản lý các cơ chế tích điểm theo từng dịch vụ mà doanh nghiệp cung cấp."
      breadcrumbItems={[{ title: "Dịch vụ" }, { title: "Cơ chế điểm" }]}
    >
      <ListToolbar
        filterDefs={FILTER_DEFS}
        filters={{
          branch: (
            <TreeSelect
              style={{ width: "100%" }}
              disabled={isLoadStoreAll}
              placeholder="Lọc theo đơn vị"
              showSearch
              allowClear
              value={branchFilter}
              filterTreeNode={(inputValue, treeNode) =>
                (treeNode.title || "").toLowerCase().includes(inputValue.toLowerCase())
              }
              onChange={handleSearch}
              treeLine
              treeData={options}
            />
          ),
        }}
        onHideFilter={() => handleSearch(undefined)}
        onClearFilters={() => handleSearch(undefined)}
        columnDefs={COLUMN_DEFS}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        loading={isLoadPointMechanisms}
        onReload={handleRefresh}
        extraLeft={
          <Tooltip title="Chú thích cơ chế dữ liệu">
            <Button
              type="text"
              icon={<InfoCircleOutlined />}
              onClick={() => setIsInfoModalOpen(true)}
            />
          </Tooltip>
        }
        actions={
          <ButtonWithPermission
            actions={["point-mechanisms.store"]}
            children={"Thêm mới"}
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={openAdd}
            noPermissionText={"Không có quyền truy cập"}
          />
        }
      />

      <CmsTableCard
        columns={displayColumns}
        loading={listPointMechanisms?.length === 0 ? isLoadPointMechanisms : false}
        dataSource={isLoadPointMechanisms ? [] : listPointMechanisms}
        rowKey="id"
        pagination={{
          onShowSizeChange: handleSizeChange,
          total: totalRecords,
          current: currentPage,
          pageSize,
          onChange: handlePageChange,
        }}
        locale={{ emptyText: <Empty description="Không có cơ chế tích điểm nào!" /> }}
        scroll={{ x: "max-content" }}
        bordered
      />

      {/* Drawer thêm/sửa cơ chế */}
      <FormAddPointMechanisms
        isModalOpen={isModalOpen}
        editRecord={editRecord}
        currentPage={currentPage}
        pageSize={pageSize}
        branchFilter={branchFilter}
        handleOk={handleOk}
        handleCancel={handleCancel}
      />

      {/* Modal chú thích dữ liệu */}
      <Modal
        title="Chú thích dữ liệu"
        open={isInfoModalOpen}
        onOk={() => setIsInfoModalOpen(false)}
        onCancel={() => setIsInfoModalOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setIsInfoModalOpen(false)}>
            OK
          </Button>,
        ]}
        width="75%"
      >
        <Table
          dataSource={[
            { id: 1, serviceName: "Hệ thống", rewardTypeName: "Tính theo phần trăm", name: "Tích điểm theo % giá trị đơn hàng", conversionRate: "5", value: "0", description: "2,000,000 VNĐ = 100,000 điểm" },
            { id: 2, serviceName: "Hệ thống", rewardTypeName: "Theo giá trị đơn hàng", name: "Thưởng cố định cho mỗi lần mua", conversionRate: "100,000", value: "1", description: "250,000 VNĐ = 2 điểm\n1,200,000 VNĐ = 12 điểm" },
            { id: 3, serviceName: "Hệ thống", rewardTypeName: "Theo số lượng", name: "Tích điểm theo số lượng sản phẩm", conversionRate: "5.00", value: "1", description: "Mua 5 sản phẩm = 1 điểm\n12 sản phẩm = 2 điểm" },
            { id: 4, serviceName: "Autocare", rewardTypeName: "Sự kiện đặc biệt", name: "Sự kiện đặt bảo dưỡng", conversionRate: "0", value: "100", description: "Tham gia sự kiện mặc định 100 điểm" },
            { id: 5, serviceName: "Autocare", rewardTypeName: "Sự kiện đặc biệt", name: "Đăng ký tài khoản", conversionRate: "0", value: "50", description: "Tạo tài khoản mặc định 50 điểm" },
          ]}
          columns={[
            { title: "ID", dataIndex: "id", key: "id", width: "5%" },
            { title: "Dịch vụ", dataIndex: "serviceName", key: "serviceName" },
            { title: "Loại cơ chế", dataIndex: "rewardTypeName", key: "rewardTypeName" },
            { title: "Tên cơ chế", dataIndex: "name", key: "name" },
            { title: "Tỷ lệ chuyển đổi", dataIndex: "conversionRate", key: "conversionRate" },
            { title: "Giá trị tích lũy", dataIndex: "value", key: "value" },
            { title: "Mô tả", dataIndex: "description", key: "description" },
          ]}
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
        />
      </Modal>
    </BrandedPageLayout>
  );
}
